import React, { useEffect, useMemo, useState } from 'react';
import { useChains } from 'wagmi';
import type { Address } from 'viem';
import { getChainIconSrc } from '../config/chainDisplay';

type Props = {
  chainId: number;
  factoryAddress: Address | undefined;
};

export function FactoryChainBanner({ chainId, factoryAddress }: Props) {
  const chains = useChains();
  const chain = useMemo(() => chains.find((c) => c.id === chainId), [chains, chainId]);
  const name = chain?.name ?? `Chain ${chainId}`;
  const explorer = chain?.blockExplorers?.default?.url;
  const [iconFailed, setIconFailed] = useState(false);

  useEffect(() => {
    setIconFailed(false);
  }, [chainId]);

  const hasFactory = Boolean(factoryAddress);
  const iconSrc = useMemo(() => getChainIconSrc(chainId), [chainId]);

  return (
    <div
      role={hasFactory ? 'status' : 'alert'}
      className={`alert ${hasFactory ? 'border border-base-300 bg-base-200/80' : 'alert-warning'} flex items-start gap-3 text-left`}
    >
      <div className="shrink-0 w-12 h-12 rounded-full bg-base-300 overflow-hidden flex items-center justify-center text-lg font-semibold">
        {!iconFailed ? (
          <img
            src={iconSrc}
            alt=""
            width={48}
            height={48}
            className="w-12 h-12 object-contain"
            decoding="async"
            onError={() => setIconFailed(true)}
          />
        ) : (
          <span className="text-base-content/80" aria-hidden>
            {name.slice(0, 1).toUpperCase()}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-semibold leading-tight">{name}</p>
        <p className="text-xs opacity-70">
          Chain ID <span className="font-mono">{chainId}</span>
        </p>
        {hasFactory ? (
          <div className="pt-1 space-y-0.5">
            <p className="text-xs opacity-70">Splitter factory</p>
            {explorer ? (
              <a
                href={`${explorer}/address/${factoryAddress}`}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-sm link link-hover break-all"
              >
                {factoryAddress}
              </a>
            ) : (
              <p className="font-mono text-sm break-all">{factoryAddress}</p>
            )}
          </div>
        ) : (
          <p className="text-sm pt-0.5">
            No factory configured for this network. Coming Soon.
        
          </p>
        )}
      </div>
    </div>
  );
}
